/*
 * Freebird Stores — Toggle product band.
 *
 * WHAT THIS FILE OWNS, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * It owns two things: the value of `data-fb-active`, and the tab
 * semantics. It does NOT own visibility. No line here writes `style`,
 * sets the `hidden` attribute, or toggles a mode class, and none should
 * be added.
 *
 * assets/freebird-toggle-product-band.css decides what is on screen,
 * from a media query. That is what makes the desktop layout correct
 * with scripting off, and the first paint correct before this module
 * runs — a mode expressed by script is wrong until the script arrives,
 * and first paint is the render no script fixes. See
 * docs/solutions/theme-architecture/
 * a-state-class-can-be-load-bearing-geometry.md and
 * render-twice-and-toggle-by-breakpoint.md.
 *
 * The `hidden` attribute could not do the job anyway: it is a normal
 * user-agent rule with no `!important`, so any author `display` beats
 * it at origin, before specificity is consulted.
 *
 * ── FOUR CONSTRAINTS THAT ARE NOT OBVIOUS FROM THE CODE ──────────────
 *
 * 1. THE TABS ARE OUTSIDE BOTH PANELS, AND THAT IS LOAD-BEARING.
 *    Selecting a tab makes the outgoing panel `display: none`. If focus
 *    were inside that panel when it went, it would fall to <body> —
 *    the defect this theme has shipped twice and the reason
 *    scripts/keyboard-walkthrough/ exists. Because focus only ever
 *    rests on a tab, and tabs are siblings of the panels rather than
 *    children, no reachable focus target is ever inside the box that
 *    disappears. Move the tabs inside a panel and that guarantee is
 *    gone with no visible symptom until someone tabs through.
 *
 * 2. SEMANTICS ARE REMOVED ABOVE 750px, NOT SET TO FALSE.
 *    `aria-selected="false"` on a control that is not a tab still
 *    announces a tab. At desktop the strip is not on the page at all,
 *    so `role`, `aria-selected` and `tabindex` all come off.
 *
 *    `aria-controls` and `aria-labelledby` are the exceptions and they
 *    STAY, because they are rendered by Liquid rather than added here
 *    and because neither asserts a widget role — they name a
 *    relationship that is still true at any width. An earlier version
 *    of this comment claimed everything came off, which was not what
 *    the code did.
 *
 * 3. NO shopify:section:load LISTENER IS NEEDED, AND ADDING ONE IS A
 *    DOUBLE-BIND. The plan called for one, reasoning from the theme
 *    editor's re-render. A custom element does not need it: a section
 *    re-render replaces this element, which fires disconnectedCallback
 *    on the old one and connectedCallback on the new. The wiring is
 *    automatic; a section:load handler would attach a second set of
 *    listeners to an element that already has them.
 *
 * 4. Enter AND Space ARE NOT HANDLED HERE. The tabs are real
 *    <button> elements, so the browser already fires `click` for both.
 *    Handling them in keydown as well would activate twice.
 *
 * ── THE 749 IS DUPLICATED AND CANNOT BE SHARED ───────────────────────
 * FB_MOBILE_QUERY below and the `max-width: 749px` block in
 * assets/freebird-toggle-product-band.css must agree. Nothing can hold
 * one constant across a Liquid-served stylesheet and a JS module.
 * Change one, change the other — otherwise the tabs are visible with no
 * semantics, or carry semantics while invisible.
 *
 * 750 is the theme's rung rather than 768; base Horizon switches there
 * in 35 rules. docs/solutions/conventions/breakpoint-ladder.md.
 */

const FB_MOBILE_QUERY = '(max-width: 749px)';

const TAB_SELECTOR = '.fb-toggle-band__tab';
const STRIP_SELECTOR = '.fb-toggle-band__tabs';
const PANEL_SELECTOR = '.fb-toggle-band__panel';

class FreebirdToggleProductBand extends HTMLElement {
  connectedCallback() {
    this.strip = this.querySelector(STRIP_SELECTOR);
    this.tabs = Array.from(this.querySelectorAll(TAB_SELECTOR));

    /*
     * A section with one collection unset renders one row and no strip.
     * That is a working section, not a broken one — it simply shows its
     * row at every width — so this returns without wiring anything and
     * without logging. Liquid also withholds `data-fb-toggle` in that
     * case, which is what stops the stylesheet hiding the only row.
     */
    if (!this.strip || this.tabs.length < 2) return;

    this.mediaQuery = window.matchMedia(FB_MOBILE_QUERY);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.mediaQuery.addEventListener('change', this.handleMediaChange);

    this.applySemantics(this.mediaQuery.matches);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }
  }

  /* Delegated from `this` rather than bound to each button, so a
     re-rendered subtree keeps working, and scoped with `contains` so a
     second instance on the same page cannot be driven from this one. */
  handleClick = (event) => {
    const tab = event.target.closest(TAB_SELECTOR);
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    if (index === -1) return;

    this.select(index, { moveFocus: true });
  };

  handleKeydown = (event) => {
    const tab = event.target.closest(TAB_SELECTOR);
    if (!tab || !this.contains(tab)) return;

    const current = this.tabs.indexOf(tab);
    if (current === -1) return;

    let next = null;

    if (event.key === 'ArrowRight') {
      next = (current + 1) % this.tabs.length;
    } else if (event.key === 'ArrowLeft') {
      next = (current - 1 + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = this.tabs.length - 1;
    }

    if (next === null) return;

    /* Home and End scroll the page by default, and the arrows can in
       some containers. The control has consumed the key. */
    event.preventDefault();

    /*
     * The arrows ACTIVATE as well as move. With exactly two options
     * there is nothing to browse past, so requiring Enter after every
     * arrow would add a keystroke and buy nothing. APG permits both
     * patterns; this is the one that suits a two-option toggle.
     */
    this.select(next, { moveFocus: true });
  };

  handleMediaChange = (event) => {
    /* A viewport change must NOT move focus — the shopper did not ask
       for it, and stealing focus on resize is its own defect. */
    this.applySemantics(event.matches);
  };

  get activeIndex() {
    const raw = parseInt(this.dataset.fbActive, 10);
    return Number.isInteger(raw) && raw >= 1 && raw <= this.tabs.length ? raw - 1 : 0;
  }

  select(index, { moveFocus = false } = {}) {
    /*
     * ORDER MATTERS. The attribute goes first, because it is what the
     * stylesheet reads to swap the panels, and the ARIA that follows
     * should describe the state that is already true. Focus goes last.
     * See rescue-focus-after-disabling-not-before.md — focusing before
     * the state settles is how a .focus() call becomes a silent no-op
     * that leaves focus on <body>.
     */
    this.dataset.fbActive = String(index + 1);

    if (this.mediaQuery && this.mediaQuery.matches) {
      this.syncTabState();
    }

    if (moveFocus) {
      this.tabs[index].focus();
    }
  }

  syncTabState() {
    const active = this.activeIndex;

    this.tabs.forEach((tab, index) => {
      const selected = index === active;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');

      /* Roving tabindex: one stop for the whole group, so Tab leaves
         the tablist rather than walking through every option. */
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
  }

  applySemantics(isMobile) {
    if (isMobile) {
      this.strip.setAttribute('role', 'tablist');
      this.tabs.forEach((tab) => tab.setAttribute('role', 'tab'));
      this.panels().forEach((panel) => panel.setAttribute('role', 'tabpanel'));
      this.syncTabState();
      return;
    }

    /* Removed, not falsified — see constraint 2 in the header. */
    this.strip.removeAttribute('role');
    this.tabs.forEach((tab) => {
      tab.removeAttribute('role');
      tab.removeAttribute('aria-selected');
      tab.removeAttribute('tabindex');
    });
    this.panels().forEach((panel) => panel.removeAttribute('role'));
  }

  panels() {
    return Array.from(this.querySelectorAll(PANEL_SELECTOR));
  }
}

if (!customElements.get('freebird-toggle-product-band')) {
  customElements.define('freebird-toggle-product-band', FreebirdToggleProductBand);
}
